import { AiOutlineLoading } from "react-icons/ai";
import {
  Accordion,
  Alert,
  Button,
  TextInput,
  Spinner,
  Badge,
  Tooltip,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import { USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { HiInformationCircle } from "react-icons/hi";
import { useQuery } from "react-query";
import { FiSearch, FiCheckCircle, FiXCircle } from "react-icons/fi";

const UserQueriesForm = ({ categories, user, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [search, setSearch] = useState("");

  const { isLoading: isLoadingPerms, data: userQueriesPermissions } = useQuery(
    ["userQueriesPermissions", user?.id],
    async () => {
      const { data: items } = await axios.get(
        `${USERS.GET_USER_QUERY_PERMISSIONS}/${user.id}`
      );
      return items.data; // [{ QueryId }]
    },
    { enabled: isOpen && !!user?.id }
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  // índice rápido dos QueryIds que o usuário já tem
  const userPermsMap = useMemo(() => {
    const map = {};
    (userQueriesPermissions || []).forEach((p) => {
      map[p.QueryId] = true;
    });
    return map;
  }, [userQueriesPermissions]);

  // Inicializa campos quando abrir / mudar permissões
  useEffect(() => {
    if (!isOpen) return;
    reset();
    (categories || []).forEach((category, catIdx) => {
      (category?.Queries || []).forEach((query, qIdx) => {
        setValue(`categories[${catIdx}].queries[${qIdx}].QueryId`, query.id);
        if (userPermsMap[query.id]) {
          setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, true);
        }
      });
    });
    setSubmitError(null);
  }, [isOpen, categories, userPermsMap, reset, setValue]);

  // Busca: normaliza pra case-insensitive
  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const searchTerm = norm(search);

  // Helpers para contagem por categoria
  const getCategoryCounts = (category, catIdx) => {
    const queries = category?.Queries || [];
    const filtered = queries.filter((q) =>
      searchTerm ? norm(q.name).includes(searchTerm) : true
    );
    const selected = filtered.filter((q, qIdx) =>
      watch(`categories[${catIdx}].queries[${qIdx}].isActive`)
    );
    return { total: filtered.length, selected: selected.length };
  };

  const selectAll = (category, catIdx) => {
    (category?.Queries || []).forEach((q, qIdx) => {
      if (!searchTerm || norm(q.name).includes(searchTerm)) {
        setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, true);
      }
    });
  };

  const clearAll = (category, catIdx) => {
    (category?.Queries || []).forEach((q, qIdx) => {
      if (!searchTerm || norm(q.name).includes(searchTerm)) {
        setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, false);
      }
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // achata categorias -> queries
      const queries = (data?.categories || [])
        .flatMap((c) => c?.queries || [])
        .filter((q) => q?.isActive)
        .map((q) => ({ QueryId: q.QueryId }));

      await axios.put(
        `${USERS.GET_USER_QUERY_PERMISSIONS}/${user.id}`,
        queries
      );

      onClose(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao atualizar permissões.";
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // UI de switch bonito usando checkbox + peer (sem state extra)
  const Switch = ({ name, label, defaultChecked }) => (
    <label className="flex items-center cursor-pointer gap-3">
      <input
        type="checkbox"
        className="sr-only peer"
        {...register(name)}
        defaultChecked={defaultChecked}
      />
      <div className="relative h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-emerald-600">
        <span className="absolute top-0.5 left-[2px] h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </label>
  );

  // Loading
  if (isLoadingPerms) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner aria-label="Carregando permissões..." />
      </div>
    );
  }

  // Empty categories
  if (!categories || categories.length === 0) {
    return (
      <div className="text-center text-sm md:text-base">
        Não há nenhuma categoria cadastrada.
        <br />
        <span className="text-slate-500">
          Comece criando categorias e consultas.
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      {/* Barra de busca */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1">
          <TextInput
            icon={FiSearch}
            type="text"
            placeholder="Buscar consulta por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <Tooltip content="Limpar busca">
          <Button color="light" type="button" onClick={() => setSearch("")}>
            Limpar
          </Button>
        </Tooltip>
      </div>

      {/* Lista por categoria */}
      <Accordion collapseAll className="rounded-2xl border border-slate-200">
        {categories.map((category, idxCat) => {
          const queries = category?.Queries || [];
          const filtered = queries.filter((q) =>
            searchTerm ? norm(q.name).includes(searchTerm) : true
          );
          const { total, selected } = getCategoryCounts(category, idxCat);

          return (
            <Accordion.Panel key={idxCat}>
              <Accordion.Title>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{category.name}</span>
                    <Badge color="gray" size="xs">
                      {selected}/{total}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Selecionar todas desta categoria (filtradas)">
                      <Button
                        size="xs"
                        color="success"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAll(category, idxCat);
                        }}
                        className="hidden sm:inline-flex"
                      >
                        <div className="flex items-center">
                          <FiCheckCircle className="mr-1" />
                          <span>Selecionar tudo</span>
                        </div>
                      </Button>
                    </Tooltip>
                    <Tooltip content="Limpar seleção desta categoria (filtradas)">
                      <Button
                        size="xs"
                        color="light"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAll(category, idxCat);
                        }}
                        className="hidden sm:inline-flex"
                      >
                        <div className="flex items-center">
                          <FiXCircle className="mr-1" />
                          <span>Limpar</span>
                        </div>
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </Accordion.Title>

              <Accordion.Content>
                {filtered.length > 0 ? (
                  <div className="space-y-3">
                    {filtered.map((query, qIdxVisible) => {
                      // precisamos do índice real para o register:
                      const realIndex = queries.findIndex(
                        (q) => q.id === query.id
                      );
                      const nameBase = `categories[${idxCat}].queries[${realIndex}]`;

                      return (
                        <div
                          key={query.id}
                          className="rounded-xl border border-slate-200 p-3"
                        >
                          {/* hidden campo QueryId */}
                          <input
                            type="hidden"
                            {...register(`${nameBase}.QueryId`)}
                            defaultValue={query.id}
                          />
                          <div className="flex items-center justify-between">
                            <Switch
                              name={`${nameBase}.isActive`}
                              label={query.name}
                              defaultChecked={!!userPermsMap[query.id]}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-slate-500">
                    Nenhuma consulta encontrada para este filtro.
                  </div>
                )}
              </Accordion.Content>
            </Accordion.Panel>
          );
        })}
      </Accordion>

      {/* Footer fixo */}
      <div className="sticky bottom-0 mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white/80 p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        {submitError && (
          <Alert color="failure" icon={HiInformationCircle}>
            {submitError}
          </Alert>
        )}
        <div className="flex items-center justify-end gap-3">
          <Button
            color="light"
            type="button"
            onClick={() => onClose(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="primary"
            isProcessing={isSubmitting}
            processingSpinner={
              <AiOutlineLoading className="h-6 w-6 animate-spin" />
            }
            disabled={isSubmitting}
          >
            Atualizar permissões
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserQueriesForm;
